import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Project } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import ProjectCard from '@/components/projects/ProjectCard';
import CreateProjectDialog from '@/components/projects/CreateProjectDialog';
import LoadingScreen from '@/components/ui/loading-screen';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FolderKanban, Plus, Search } from 'lucide-react';
import EditorTabBar, { EditorTabItem } from './EditorTabBar';
import AIProjectWorkspace, { AIProjectWorkspaceState, AIProjectWorkspaceTab } from './AIProjectWorkspace';

interface AIProjectsProps {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  initialProjectId?: string;
}

type ProjectsTab = {
  id: string;
  kind: 'home' | 'project';
  title: string;
  path?: string;
  projectId?: string;
};

type ProjectsTabState = {
  tabs: ProjectsTab[];
  activeId: string;
};

type PersistedProjectsTabState = {
  tabs?: ProjectsTab[];
  activeId?: string;
};

const HOME_TAB: ProjectsTab = {
  id: 'home',
  kind: 'home',
  title: 'Home',
};

const WORKSPACE_OVERVIEW_TAB: AIProjectWorkspaceTab = {
  id: 'overview',
  kind: 'overview',
  title: 'Overview',
  path: 'overview',
};

const createProjectTab = (projectId: string, title?: string | null): ProjectsTab => ({
  id: projectId,
  kind: 'project',
  title: title?.trim() || 'Untitled Project',
  path: `/projects/${projectId}`,
  projectId,
});

const createWorkspaceChatTab = (chatId: string, title?: string | null): AIProjectWorkspaceTab => ({
  id: `chat:${chatId}`,
  kind: 'chat',
  title: title?.trim() || 'Untitled Chat',
  path: `/chat/${chatId}`,
  chatId,
});

const createWorkspaceDocumentTab = (title: string, filePath: string, initialContent?: string): AIProjectWorkspaceTab => ({
  id: `document:${encodeURIComponent(filePath)}`,
  kind: 'document',
  title: title?.trim() || 'Untitled Document',
  path: filePath,
  filePath,
  initialContent,
});

const ensureHomeTab = (tabs: ProjectsTab[]) => [HOME_TAB, ...tabs.filter((tab) => tab.id !== HOME_TAB.id)];
const ensureWorkspaceOverviewTab = (tabs: AIProjectWorkspaceTab[]) => [WORKSPACE_OVERVIEW_TAB, ...tabs.filter((tab) => tab.id !== WORKSPACE_OVERVIEW_TAB.id)];
const createInitialWorkspaceState = (): AIProjectWorkspaceState => ({
  tabs: [WORKSPACE_OVERVIEW_TAB],
  activeId: WORKSPACE_OVERVIEW_TAB.id,
});

const AIProjects = ({ onOpenWindow, initialProjectId }: AIProjectsProps) => {
  const { user } = useAuth();
  const { projects, chats, fetchProjects, fetchModels, refreshChats } = useSupabaseStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});
  const [workspaceStates, setWorkspaceStates] = useState<Record<string, AIProjectWorkspaceState>>({});
  const [{ tabs, activeId }, setTabState] = useState<ProjectsTabState>({
    tabs: [HOME_TAB],
    activeId: HOME_TAB.id,
  });
  const restoredUserIdRef = useRef<string | null>(null);
  const initialProjectOpenedRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!user) {
        if (!cancelled) {
          setIsLoadingProjects(false);
        }
        return;
      }

      setIsLoadingProjects(true);

      try {
        await Promise.all([fetchProjects(user.id), fetchModels(), refreshChats()]);
      } finally {
        if (!cancelled) {
          setIsLoadingProjects(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [fetchModels, fetchProjects, refreshChats, user]);

  useEffect(() => {
    if (!user) {
      setWorkspaceStates({});
    }
  }, [user]);

  useEffect(() => {
    const fetchDocCounts = async () => {
      if (!user?.id || projects.length === 0) {
        setDocCounts({});
        return;
      }

      const projectIds = projects.map((project) => project.id);
      const { data, error } = await (supabase as any)
        .from('mapleword_project_documents')
        .select('project_id')
        .eq('user_id', user.id)
        .in('project_id', projectIds);

      if (error) {
        setDocCounts({});
        return;
      }

      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        counts[row.project_id] = (counts[row.project_id] || 0) + 1;
      });
      setDocCounts(counts);
    };

    fetchDocCounts();
  }, [projects, user?.id]);

  const projectMap = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
  const storageKey = user ? `ai-projects-tabs:${user.id}` : null;

  const updateWorkspaceState = useCallback((projectId: string, updater: (state: AIProjectWorkspaceState) => AIProjectWorkspaceState) => {
    setWorkspaceStates((previous) => {
      const current = previous[projectId] || createInitialWorkspaceState();
      return {
        ...previous,
        [projectId]: updater(current),
      };
    });
  }, []);

  const ensureWorkspace = useCallback((projectId: string) => {
    setWorkspaceStates((previous) => {
      if (previous[projectId]) {
        return previous;
      }

      return {
        ...previous,
        [projectId]: createInitialWorkspaceState(),
      };
    });
  }, []);

  useEffect(() => {
    if (!user) {
      restoredUserIdRef.current = null;
      setTabState({ tabs: [HOME_TAB], activeId: HOME_TAB.id });
      return;
    }

    if (isLoadingProjects || restoredUserIdRef.current === user.id) {
      return;
    }

    let nextState: ProjectsTabState = { tabs: [HOME_TAB], activeId: HOME_TAB.id };

    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as PersistedProjectsTabState;
          const restoredTabs = (parsed.tabs || [])
            .filter((tab): tab is ProjectsTab => tab.kind === 'project' && Boolean(tab.projectId))
            .map((tab) => {
              const project = projectMap.get(tab.projectId!);
              return project ? createProjectTab(project.id, project.name) : null;
            })
            .filter((tab): tab is ProjectsTab => Boolean(tab));

          const normalizedTabs = ensureHomeTab(restoredTabs);
          nextState = {
            tabs: normalizedTabs,
            activeId:
              parsed.activeId && normalizedTabs.some((tab) => tab.id === parsed.activeId)
                ? parsed.activeId
                : HOME_TAB.id,
          };
        }
      } catch (error) {
        console.error('Error restoring AI project tabs:', error);
      }
    }

    restoredUserIdRef.current = user.id;
    setTabState(nextState);
  }, [isLoadingProjects, projectMap, storageKey, user]);

  useEffect(() => {
    if (!user || restoredUserIdRef.current !== user.id || isLoadingProjects) {
      return;
    }

    setTabState((previousState) => {
      let changed = false;

      const syncedTabs = ensureHomeTab(
        previousState.tabs.flatMap((tab) => {
          if (tab.kind === 'home') {
            return [HOME_TAB];
          }

          if (!tab.projectId) {
            changed = true;
            return [];
          }

          const project = projectMap.get(tab.projectId);
          if (!project) {
            changed = true;
            return [];
          }

          const nextTab = createProjectTab(project.id, project.name);
          if (tab.title !== nextTab.title || tab.path !== nextTab.path) {
            changed = true;
          }

          return [nextTab];
        })
      );

      const nextActiveId = syncedTabs.some((tab) => tab.id === previousState.activeId)
        ? previousState.activeId
        : HOME_TAB.id;

      if (!changed && syncedTabs.length === previousState.tabs.length && nextActiveId === previousState.activeId) {
        return previousState;
      }

      return {
        tabs: syncedTabs,
        activeId: nextActiveId,
      };
    });
  }, [isLoadingProjects, projectMap, user]);

  useEffect(() => {
    if (!storageKey || !user || restoredUserIdRef.current !== user.id) {
      return;
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        tabs: tabs.filter((tab) => tab.kind === 'project'),
        activeId: tabs.some((tab) => tab.id === activeId) ? activeId : HOME_TAB.id,
      } satisfies PersistedProjectsTabState)
    );
  }, [activeId, storageKey, tabs, user]);

  const openProjectTab = useCallback((projectId: string, title?: string | null) => {
    ensureWorkspace(projectId);

    setTabState((previousState) => {
      const nextTitle = title?.trim() || projectMap.get(projectId)?.name || 'Untitled Project';
      const existingTab = previousState.tabs.find((tab) => tab.id === projectId);

      if (existingTab) {
        return {
          tabs: previousState.tabs.map((tab) => (tab.id === projectId ? createProjectTab(projectId, nextTitle) : tab)),
          activeId: projectId,
        };
      }

      return {
        tabs: [...previousState.tabs, createProjectTab(projectId, nextTitle)],
        activeId: projectId,
      };
    });
  }, [ensureWorkspace, projectMap]);

  const openProjectChatTab = useCallback((projectId: string, chatId: string, title?: string) => {
    updateWorkspaceState(projectId, (current) => {
      const nextTab = createWorkspaceChatTab(chatId, title);
      const existingTab = current.tabs.find((tab) => tab.id === nextTab.id);

      return {
        tabs: existingTab
          ? current.tabs.map((tab) => (tab.id === nextTab.id ? nextTab : tab))
          : [...ensureWorkspaceOverviewTab(current.tabs), nextTab],
        activeId: nextTab.id,
      };
    });
  }, [updateWorkspaceState]);

  const openProjectDocumentTab = useCallback((projectId: string, title: string, filePath: string, initialContent?: string) => {
    updateWorkspaceState(projectId, (current) => {
      const nextTab = createWorkspaceDocumentTab(title, filePath, initialContent);
      const existingTab = current.tabs.find((tab) => tab.id === nextTab.id);

      return {
        tabs: existingTab
          ? current.tabs.map((tab) => (tab.id === nextTab.id ? nextTab : tab))
          : [...ensureWorkspaceOverviewTab(current.tabs), nextTab],
        activeId: nextTab.id,
      };
    });
  }, [updateWorkspaceState]);

  useEffect(() => {
    if (!initialProjectId || !user || isLoadingProjects) {
      return;
    }

    const project = projectMap.get(initialProjectId);
    if (!project) {
      return;
    }

    const initialProjectKey = `${user.id}:${initialProjectId}`;
    if (initialProjectOpenedRef.current === initialProjectKey) {
      return;
    }

    initialProjectOpenedRef.current = initialProjectKey;
    openProjectTab(project.id, project.name);
  }, [initialProjectId, isLoadingProjects, openProjectTab, projectMap, user]);

  const closeTab = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const tabIndex = previousState.tabs.findIndex((tab) => tab.id === id);
      if (tabIndex === -1) {
        return previousState;
      }

      const nextTabs = ensureHomeTab(previousState.tabs.filter((tab) => tab.id !== id));
      const nextActiveId =
        previousState.activeId === id
          ? previousState.tabs
              .slice(0, tabIndex)
              .reverse()
              .find((tab) => tab.id !== id)?.id || HOME_TAB.id
          : nextTabs.some((tab) => tab.id === previousState.activeId)
            ? previousState.activeId
            : HOME_TAB.id;

      return {
        tabs: nextTabs,
        activeId: nextActiveId,
      };
    });
  }, []);

  const closeOthers = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const selectedTab = previousState.tabs.find((tab) => tab.id === id);
      if (!selectedTab) {
        return previousState;
      }

      return {
        tabs: ensureHomeTab([selectedTab]),
        activeId: selectedTab.id,
      };
    });
  }, []);

  const closeRight = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const selectedIndex = previousState.tabs.findIndex((tab) => tab.id === id);
      if (selectedIndex === -1) {
        return previousState;
      }

      const nextTabs = ensureHomeTab(
        previousState.tabs.filter((tab, index) => tab.id === HOME_TAB.id || index <= selectedIndex)
      );

      return {
        tabs: nextTabs,
        activeId: nextTabs.some((tab) => tab.id === previousState.activeId) ? previousState.activeId : id,
      };
    });
  }, []);

  const reorderTabs = useCallback((orderedIds: string[]) => {
    setTabState((previousState) => {
      const projectTabs = previousState.tabs.filter((tab) => tab.kind === 'project');
      const projectTabMap = new Map(projectTabs.map((tab) => [tab.id, tab]));
      const orderedProjectTabs = orderedIds
        .filter((id) => id !== HOME_TAB.id)
        .map((id) => projectTabMap.get(id))
        .filter((tab): tab is ProjectsTab => Boolean(tab));
      const remainingTabs = projectTabs.filter((tab) => !orderedProjectTabs.some((orderedTab) => orderedTab.id === tab.id));

      return {
        ...previousState,
        tabs: [HOME_TAB, ...orderedProjectTabs, ...remainingTabs],
      };
    });
  }, []);

  const closeWorkspaceTab = useCallback((projectId: string, tabId: string) => {
    if (tabId === WORKSPACE_OVERVIEW_TAB.id) {
      return;
    }

    updateWorkspaceState(projectId, (current) => {
      const tabIndex = current.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) {
        return current;
      }

      const nextTabs = ensureWorkspaceOverviewTab(current.tabs.filter((tab) => tab.id !== tabId));
      const nextActiveId =
        current.activeId === tabId
          ? current.tabs
              .slice(0, tabIndex)
              .reverse()
              .find((tab) => tab.id !== tabId)?.id || WORKSPACE_OVERVIEW_TAB.id
          : nextTabs.some((tab) => tab.id === current.activeId)
            ? current.activeId
            : WORKSPACE_OVERVIEW_TAB.id;

      return {
        tabs: nextTabs,
        activeId: nextActiveId,
      };
    });
  }, [updateWorkspaceState]);

  const closeWorkspaceOthers = useCallback((projectId: string, tabId: string) => {
    if (tabId === WORKSPACE_OVERVIEW_TAB.id) {
      return;
    }

    updateWorkspaceState(projectId, (current) => {
      const selectedTab = current.tabs.find((tab) => tab.id === tabId);
      if (!selectedTab) {
        return current;
      }

      return {
        tabs: ensureWorkspaceOverviewTab([selectedTab]),
        activeId: selectedTab.id,
      };
    });
  }, [updateWorkspaceState]);

  const closeWorkspaceRight = useCallback((projectId: string, tabId: string) => {
    if (tabId === WORKSPACE_OVERVIEW_TAB.id) {
      return;
    }

    updateWorkspaceState(projectId, (current) => {
      const selectedIndex = current.tabs.findIndex((tab) => tab.id === tabId);
      if (selectedIndex === -1) {
        return current;
      }

      const nextTabs = ensureWorkspaceOverviewTab(
        current.tabs.filter((tab, index) => tab.id === WORKSPACE_OVERVIEW_TAB.id || index <= selectedIndex)
      );

      return {
        tabs: nextTabs,
        activeId: nextTabs.some((tab) => tab.id === current.activeId) ? current.activeId : tabId,
      };
    });
  }, [updateWorkspaceState]);

  const reorderWorkspaceTabs = useCallback((projectId: string, orderedIds: string[]) => {
    updateWorkspaceState(projectId, (current) => {
      const childTabs = current.tabs.filter((tab) => tab.kind !== 'overview');
      const childTabMap = new Map(childTabs.map((tab) => [tab.id, tab]));
      const orderedChildTabs = orderedIds
        .filter((id) => id !== WORKSPACE_OVERVIEW_TAB.id)
        .map((id) => childTabMap.get(id))
        .filter((tab): tab is AIProjectWorkspaceTab => Boolean(tab));
      const remainingTabs = childTabs.filter((tab) => !orderedChildTabs.some((orderedTab) => orderedTab.id === tab.id));

      return {
        ...current,
        tabs: [WORKSPACE_OVERVIEW_TAB, ...orderedChildTabs, ...remainingTabs],
      };
    });
  }, [updateWorkspaceState]);

  const popOutTab = useCallback((id: string) => {
    const tab = tabs.find((candidate) => candidate.id === id && candidate.kind === 'project');
    if (!tab?.projectId) {
      return;
    }

    onOpenWindow?.('ai-projects', `AI Projects - ${tab.title}`, tab.projectId);
  }, [onOpenWindow, tabs]);

  const projectsWithCounts = useMemo(() => {
    return projects.map((project: Project) => ({
      ...project,
      chatCount: chats.filter((chat) => chat.project_id === project.id).length,
      docCount: docCounts[project.id] || 0,
    }));
  }, [chats, docCounts, projects]);

  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) {
      return projectsWithCounts;
    }

    const query = searchTerm.toLowerCase();
    return projectsWithCounts.filter((project) => {
      return (
        project.name.toLowerCase().includes(query) ||
        (project.description || '').toLowerCase().includes(query)
      );
    });
  }, [projectsWithCounts, searchTerm]);

  const editorTabs = useMemo<EditorTabItem[]>(() => {
    return tabs.map((tab) => ({
      id: tab.id,
      name: tab.title,
      path: tab.path || '',
      isPinned: tab.kind === 'home',
    }));
  }, [tabs]);

  const activeTab = useMemo(() => {
    return tabs.find((tab) => tab.id === activeId) || HOME_TAB;
  }, [activeId, tabs]);

  const activeWorkspaceState = activeTab.projectId
    ? workspaceStates[activeTab.projectId] || createInitialWorkspaceState()
    : createInitialWorkspaceState();

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <p className="text-sm text-muted-foreground">Sign in to view your AI projects in MapleOS.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="pt-[5px]">
        <EditorTabBar
          tabs={editorTabs}
          activeId={activeId}
          onSelect={(id) => setTabState((previousState) => ({ ...previousState, activeId: id }))}
          onClose={closeTab}
          onReorder={reorderTabs}
          onNewUntitled={() => setCreateDialogOpen(true)}
          onCloseOthers={closeOthers}
          onCloseRight={closeRight}
          onSave={() => undefined}
          onSaveAll={() => undefined}
          onRevealInExplorer={() => undefined}
          onCopyPath={() => undefined}
          onPopOut={popOutTab}
          showSaveItems={false}
          showRevealItems={false}
        />
      </div>

      <div className="min-h-0 flex-1">
        {activeTab.kind === 'home' ? (
          <div className="h-full overflow-y-auto">
            <div className="mx-auto flex min-h-full max-w-7xl flex-col gap-6 p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="flex items-center gap-3 text-3xl font-bold text-foreground">
                    <FolderKanban className="h-8 w-8 text-primary" />
                    AI Projects
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    Organize your AI workspaces, chats, documents, and knowledge.
                  </p>
                </div>

                <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  New Project
                </Button>
              </div>

              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative w-full max-w-xl">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search AI projects..."
                    className="pl-9"
                  />
                </div>

                <div className="text-sm text-muted-foreground">
                  {projectsWithCounts.length} {projectsWithCounts.length === 1 ? 'project' : 'projects'}
                </div>
              </div>

              {isLoadingProjects ? (
                <div className="flex flex-1 items-center justify-center py-12">
                  <LoadingScreen />
                </div>
              ) : filteredProjects.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {filteredProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onOpenProject={(selectedProject) => openProjectTab(selectedProject.id, selectedProject.name)}
                    />
                  ))}
                </div>
              ) : projectsWithCounts.length > 0 ? (
                <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-6 py-16 text-center">
                  <h2 className="text-xl font-semibold">No matching AI projects</h2>
                  <p className="mt-2 text-muted-foreground">Try a different search term.</p>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-6 py-16 text-center">
                  <FolderKanban className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h2 className="mt-4 text-xl font-semibold">No AI projects yet</h2>
                  <p className="mt-2 text-muted-foreground">Create your first AI project to organize chats and documents.</p>
                  <Button onClick={() => setCreateDialogOpen(true)} className="mt-6 gap-2">
                    <Plus className="h-4 w-4" />
                    Create Project
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <AIProjectWorkspace
            key={activeTab.id}
            projectId={activeTab.projectId || ''}
            state={activeWorkspaceState}
            onSelect={(tabId) => updateWorkspaceState(activeTab.projectId || '', (current) => ({ ...current, activeId: tabId }))}
            onClose={(tabId) => closeWorkspaceTab(activeTab.projectId || '', tabId)}
            onReorder={(orderedIds) => reorderWorkspaceTabs(activeTab.projectId || '', orderedIds)}
            onCloseOthers={(tabId) => closeWorkspaceOthers(activeTab.projectId || '', tabId)}
            onCloseRight={(tabId) => closeWorkspaceRight(activeTab.projectId || '', tabId)}
            onNewChat={() => updateWorkspaceState(activeTab.projectId || '', (current) => ({ ...current, activeId: WORKSPACE_OVERVIEW_TAB.id }))}
            onOpenChat={(chatId, title) => openProjectChatTab(activeTab.projectId || '', chatId, title)}
            onOpenDocument={(title, filePath, initialContent) => openProjectDocumentTab(activeTab.projectId || '', title, filePath, initialContent)}
          />
        )}
      </div>

      <CreateProjectDialog open={isCreateDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  );
};

export default AIProjects;
